import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wallet, Users, Plus } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { auth, clients, payroll, getStoredUser } from '../api';
import { PayrollPanel } from '../components/payroll/PayrollPanel';

export default function PayrollPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [user, setUser] = useState(getStoredUser());
  const [list, setList] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const selectedId = params.get('client') || '';

  const load = () => Promise.all([payroll.clients(), clients.list()]).then(([p, c]) => {
    setList(p); setAllClients(c);
    if (!params.get('client') && p[0]) setParams({ client: p[0].id }, { replace: true });
  });
  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    auth.me().then(setUser).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = allClients.find((c) => c.id === selectedId);
  const others = allClients.filter((c) => !c.payrollEnabled);
  const add = (cid) => payroll.enable(cid, true).then(() => { toast({ title: 'Payroll enabled' }); setParams({ client: cid }); load(); });

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Payroll"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div>
            <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><Wallet size={24} className="text-emerald-800"/> Payroll</h1>
            <p className="mt-1 text-stone-600 text-sm">Each client&apos;s payroll is kept separate: pick a client to manage its employees, pay runs and SARS submissions.</p>
          </div>
          <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
            <aside className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="payroll-client-list">
              <div className="px-4 py-3 border-b border-stone-100 text-xs uppercase tracking-wider font-semibold text-stone-500">Payroll clients · {list.length}</div>
              {list.length === 0 && <div className="p-4 text-sm text-stone-500">No payroll clients yet.</div>}
              {list.map((c) => (
                <button key={c.id} type="button" onClick={() => setParams({ client: c.id })} data-testid={`payroll-client-${c.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-stone-50 transition-colors ${c.id === selectedId ? 'bg-emerald-50 border-l-4 border-l-emerald-700' : 'hover:bg-stone-50'}`}>
                  <div className="font-medium text-stone-900 text-sm">{c.name}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1"><Users size={11}/> {c.employees} employee{c.employees === 1 ? '' : 's'}{c.lastRun ? ` · last: ${c.lastRun.periodLabel}` : ''}</div>
                </button>
              ))}
              {others.length > 0 && (
                <div className="p-3">
                  <Select value="" onValueChange={add}>
                    <SelectTrigger className="h-9 text-sm" data-testid="payroll-add-client-select"><Plus size={14} className="mr-1"/><SelectValue placeholder="Add a client to payroll"/></SelectTrigger>
                    <SelectContent>{others.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </aside>
            <section className="min-w-0">
              {selected ? (
                <>
                  <div className="mb-4"><div className="text-xl font-serif font-semibold text-stone-900" data-testid="payroll-selected-client">{selected.name}</div><div className="text-xs text-stone-500">PAYE ref {selected.payeNumber || '—'} · SDL {selected.sdlRegistered ? 'registered' : 'not registered'}</div></div>
                  <PayrollPanel key={selected.id} client={selected} onEnabled={load}/>
                </>
              ) : <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-stone-500">Choose or add a payroll client.</div>}
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}
